'use client';

import Icon from '@/components/ui/AppIcon';

interface PayoutHistoryProps {
  dateRange: string;
}

const PayoutHistory = ({ dateRange }: PayoutHistoryProps) => {
  const payouts = [
    {
      id: 'PAY-2026-001',
      date: '2026-02-01',
      amount: 1234.56,
      status: 'completed',
      method: 'Bank Transfer',
      period: 'January 2026',
      bookings: 67,
    },
    {
      id: 'PAY-2026-002',
      date: '2026-01-01',
      amount: 987.40,
      status: 'completed',
      method: 'Bank Transfer',
      period: 'December 2025',
      bookings: 54,
    },
    {
      id: 'PAY-2025-012',
      date: '2025-12-01',
      amount: 1456.80,
      status: 'completed',
      method: 'Bank Transfer',
      period: 'November 2025',
      bookings: 78,
    },
  ];

  const pendingPayout = {
    amount: 423.60,
    bookings: 32,
    nextPayoutDate: '2026-03-01',
  };

  return (
    <div className="space-y-6">
      {/* Pending Payout Card */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-bold text-lg text-foreground mb-1">Pending Payout</h3>
            <p className="text-sm text-muted-foreground">
              Next payout scheduled for {new Date(pendingPayout.nextPayoutDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <div className="p-3 bg-white rounded-lg">
            <Icon name="ClockIcon" size={24} className="text-primary" />
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-black text-foreground mb-1">£{pendingPayout.amount.toFixed(2)}</p>
            <p className="text-sm text-muted-foreground">{pendingPayout.bookings} bookings</p>
          </div>
          <button className="px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-colors">
            View Details
          </button>
        </div>
      </div>

      {/* Payout History */}
      <div>
        <h3 className="font-bold text-lg text-foreground mb-4">Payout History</h3>
        <div className="space-y-3">
          {payouts.map((payout) => (
            <div
              key={payout.id}
              className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <Icon name="CheckCircleIcon" size={24} className="text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-foreground">{payout.id}</h4>
                      <span className="text-xs font-bold px-2 py-1 rounded bg-green-100 text-green-700">
                        {payout.status}
                      </span>
                    </div>
                    <p className="text-sm text-foreground mb-1">{payout.period}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="CalendarIcon" size={14} />
                        {new Date(payout.date).toLocaleDateString('en-GB')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="BanknotesIcon" size={14} />
                        {payout.method}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="TicketIcon" size={14} />
                        {payout.bookings} bookings
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground mb-1">Payout Amount</p>
                    <p className="text-2xl font-black text-green-600">£{payout.amount.toFixed(2)}</p>
                  </div>
                  <button className="p-2 hover:bg-surface rounded-lg transition-colors">
                    <Icon name="DocumentArrowDownIcon" size={20} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white border border-border rounded-lg p-6">
        <h3 className="font-bold text-lg text-foreground mb-4">Lifetime Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-surface rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Total Payouts</p>
            <p className="text-2xl font-black text-foreground">£3,678.76</p>
          </div>
          <div className="text-center p-4 bg-surface rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Total Bookings</p>
            <p className="text-2xl font-black text-foreground">199</p>
          </div>
          <div className="text-center p-4 bg-surface rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Avg Payout</p>
            <p className="text-2xl font-black text-foreground">£1,226.25</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutHistory;
